// src/services/apiClient.js
import axios from "axios";

const BASE_URL = "https://recon.jcinformatics.com/";

// Helpers
const getAccessToken = () => localStorage.getItem("accessToken");
const getRefreshToken = () => localStorage.getItem("refreshToken");
const setAccessToken = (t) => localStorage.setItem("accessToken", t);

const clearAuth = () => {
	localStorage.removeItem("accessToken");
	localStorage.removeItem("refreshToken");
	localStorage.removeItem("expiresIn");
	// Si guardas más cosas de usuario, puedes limpiarlas aquí también
};

export const api = axios.create({
	baseURL: BASE_URL,
	timeout: 20000,
});

// ---- REQUEST: adjunta Authorization Bearer ----
api.interceptors.request.use(
	(config) => {
		const token = getAccessToken();
		if (token) config.headers.Authorization = `Bearer ${token}`;
		return config;
	},
	(error) => Promise.reject(error)
);

// ---- REFRESH LOCK para evitar 5 refresh simultáneos ----
let isRefreshing = false;
let refreshQueue = [];

const resolveQueue = (error, token = null) => {
	refreshQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
	refreshQueue = [];
};

// ---- RESPONSE: si 401 => refresh y reintenta ----
api.interceptors.response.use(
	(response) => {
		// ⛔ Caso 200 pero sesión inválida
		if (
			response?.data?.success === false &&
			response?.data?.message === "Sesión no verificada."
		) {
			return Promise.reject({
				config: response.config,
				response: {
					status: 401,
					data: response.data,
				},
			});
		}

		return response;
	},
	async (error) => {
		const status = error?.response?.status;
		const originalRequest = error.config;

		// ❌ No es error de auth
		if (status !== 401) {
			return Promise.reject(error);
		}

		// 🔁 Evitar loop infinito
		if (originalRequest._retry) {
			clearAuth();
			return Promise.reject(error);
		}
		originalRequest._retry = true;

		const refreshToken = getRefreshToken();
		if (!refreshToken) {
			clearAuth();
			return Promise.reject(error);
		}

		// 🔒 Refresh lock
		if (isRefreshing) {
			return new Promise((resolve, reject) => {
				refreshQueue.push({ resolve, reject });
			}).then((newToken) => {
				originalRequest.headers.Authorization = `Bearer ${newToken}`;
				return api(originalRequest);
			});
		}

		isRefreshing = true;

		try {
			const refreshResp = await axios.post(
				`${BASE_URL}refresh-token.php`,
				{ refreshToken },
				{ headers: { "Content-Type": "application/json" } }
			);

			const newAccessToken = refreshResp.data?.accessToken;
			if (!newAccessToken) throw new Error("Refresh sin accessToken");

			setAccessToken(newAccessToken);
			resolveQueue(null, newAccessToken);

			originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
			return api(originalRequest);
		} catch (refreshErr) {
			resolveQueue(refreshErr, null);
			clearAuth();
			return Promise.reject(refreshErr);
		} finally {
			isRefreshing = false;
		}
	}
);

